<template>
  <div class="plan-card">
    <!-- 가격 헤더 -->
    <div class="price-header">
      <div class="plan-info">
        <!-- 사장님들이 가장 많이 선택한 플랜 -->
        <div v-if="planData.id === 6" class="most-selected-plan">
          <span>사장님들이 <span class="highlight">가장 많이 선택</span>했어요!</span>
        </div>

        <div class="plan-title-wrapper">
          <div class="plan-title-info">
            <div class="plan-title">{{ planData.title || '1개월 플랜' }}</div>
            <div v-if="planData.discount" class="discount-label">{{ planData.discount }}% 할인</div>
          </div>

          <v-radio color="primary-light" :value="planData.id" :ripple="false" class="radio-button"></v-radio>
        </div>

        <div class="price-wrapper">
          <div class="price-info">
            <span class="amount">{{ planData.price || '15만원' }}</span>
            <span class="unit">/월</span>
            <span class="vat-info">VAT 별도</span>
          </div>
          <span v-if="planData.originalPrice" class="original-price">{{ planData.originalPrice }}</span>
        </div>
      </div>
    </div>

    <!-- 구분선 -->
    <div class="divider"></div>

    <!-- 기능 목록 -->
    <div class="features-list">
      <div v-for="(feature, index) in computedFeatures" :key="`feature-${index}`">
        <div
          v-if="feature.value"
          class="feature-item"
          :class="{
            'last-visible': isLastVisibleFeature(index),
            'first-visible': isFirstVisibleFeature(index),
            'has-description': feature.description,
          }">
          <div class="feature-content">
            <div class="feature-title" :class="{ 'has-label': feature.label }">
              <span class="feature-title-text">{{ feature.title }}</span>
              <v-tooltip bottom nudge-top="10" transition="fade-transition">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" size="20" color="disabled-dark">mdi-help-circle-outline</v-icon>
                </template>
                <span>{{ feature.tooltip }}</span>
              </v-tooltip>
              <div v-if="feature.label" class="feature-label">
                <span class="feature-label-text">{{ feature.label }}</span>
              </div>
            </div>
            <div v-if="feature.description" class="feature-description">
              {{ feature.description }}
            </div>
            <div v-if="feature.tooltip && showTooltip === index" class="tooltip">
              {{ feature.tooltip }}
            </div>
          </div>

          <!-- 값 표시 (8명, 1회, 2회 등) -->
          <div v-if="feature.type === 'value'" class="feature-value">
            {{ feature.value || '-' }}
          </div>

          <!-- 체크 표시 -->
          <div v-else-if="feature.type === 'check' && feature.value === true" class="feature-check">
            <div class="check-icon">
              <img :src="require('@/assets/img/landing/price/mdi-check.png')" alt="체크" />
            </div>
          </div>
        </div>

        <!-- 마지막 항목이 아니면 구분선 -->
        <!-- <div v-if="index < computedFeatures.length - 1" :key="`divider-${index}`" class="divider-thin"></div> -->
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PlanCardMobile',
  props: {
    features: {
      type: Array,
      default: () => [],
    },
    planData: {
      type: Object,
      default: () => ({
        title: '1개월 플랜',
        price: '15만원',
        originalPrice: null,
        discount: null,
        selected: false,
        features: [],
      }),
    },
    selectedPlan: {
      type: Number,
      required: true,
    },
  },

  methods: {
    isFirstVisibleFeature(index) {
      return index === 0;
    },
    isLastVisibleFeature(index) {
      // 현재 인덱스부터 뒤로 검사해서 value가 있는 마지막 항목인지 확인
      for (let i = this.computedFeatures.length - 1; i > index; i--) {
        if (this.computedFeatures[i].value) {
          return false; // 뒤에 더 보이는 항목이 있으면 마지막이 아님
        }
      }
      return true; // 뒤에 보이는 항목이 없으면 마지막임
    },
  },
  computed: {
    computedFeatures() {
      return this.features.map((feature, index) => {
        const planFeature = this.planData.features[index];

        // 기본 feature 정보에 planData에서 오는 값을 매칭
        const computedFeature = {
          ...feature,
          type: planFeature?.type || 'check',
          value: planFeature?.value,
        };

        // 특별 처리: "추가혜택" 라벨 조건부 추가
        if (index >= 8 && planFeature?.value === true) {
          computedFeature.label = '추가혜택';
        }

        // 값이 숫자인 경우 단위 추가
        if (feature.title === '최소 모집 인원 보장' && typeof planFeature?.value === 'number') {
          computedFeature.value = `${planFeature.value}명`;
        } else if (feature.title === '광고 일시중단' && typeof planFeature?.value === 'number') {
          computedFeature.value = `${planFeature.value}회`;
        }

        return computedFeature;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.plan-card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px 24px 16px 24px;
  gap: 16px;
  background: var(--inversed, #fff);
  border: 1px solid var(--disabled_light, #f5f5f5);
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.price-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  gap: 8px;
}

.plan-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
}

.plan-title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.most-selected-plan {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  height: 34px;
  border-radius: 8px;
  background: rgba(234, 42, 58, 0.05);
  color: var(--default-dark-color);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  width: 100%;
}

.highlight {
  color: var(--primary-light-color);
  font-weight: 700;
}

.plan-title-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.plan-title {
  font-family: 'Spoqa Han Sans Neo', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: var(--default-dark-color);
}

.discount-label {
  background: var(--tertiary-light-color);
  color: var(--inversed-1-color, #fff);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
}

.original-price {
  font-family: 'Spoqa Han Sans Neo', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1;
  color: var(--disabled_dark-color, #c2c2c2);
  text-decoration: line-through;
  margin-left: 8px;
  vertical-align: baseline;
}

.price-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
}

.price-info {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  margin-right: 8px;
}

.amount {
  font-family: 'Spoqa Han Sans Neo', sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 28px; /* 140% */
  color: var(--default_dark, #202020);
}

.unit {
  font-family: 'Spoqa Han Sans Neo', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1;
  color: var(--default_dark, #202020);
  vertical-align: baseline;
}

.vat-info {
  font-family: 'Spoqa Han Sans Neo', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1;
  color: var(--default_light, #808080);
  vertical-align: baseline;
}

.radio-button {
  margin-left: auto;
}

.divider {
  width: 100%;
  height: 1px;
  background: var(--disabled, #dbdbdb);
}

.divider-thin {
  width: 100%;
  height: 1px;
  background: #f5f5f5;
}

.features-list {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0;
}

.feature-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  min-height: 52px;
  position: relative;
  border-bottom: 1px solid #dbdbdb;

  &.first-visible {
    padding-top: 0;
  }

  &.last-visible {
    border-bottom: none;
  }

  &.has-description {
    padding: 7px 0;
  }

  &.first-visible.has-description {
    padding-top: 0;
    padding-bottom: 7px;
  }
}

.feature-content {
  flex: 1;
  position: relative;
}

.feature-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-family: 'Spoqa Han Sans Neo', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--default-2-color);

  &.has-label {
    gap: 4px;
  }
}

.feature-title-text {
  flex-wrap: wrap;
}

.help-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.feature-description {
  font-family: 'Spoqa Han Sans Neo', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  color: var(--default-light-color);
}

.feature-value {
  font-family: 'Spoqa Han Sans Neo', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--default-2-color);
  min-width: 22px;
  text-align: center;
}

.feature-check {
  width: 22px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feature-none {
  width: 22px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Spoqa Han Sans Neo', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--default-light-color);
}

.check-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.feature-label {
  display: flex;
  padding: 2px 8px;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  border-radius: 10px;
  background: rgba(234, 42, 58, 0.1);
}

.feature-label-text {
  color: var(--primary_light, #ea2a3a);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 133.333% */
}

.selected {
  background: var(--inversed, #f2f2f2);
}

// v-radio
::v-deep .v-radio {
  margin: 0 !important;

  .v-input--selection-controls__input {
    margin: 0 !important;
  }

  .v-input--selection-controls__ripple {
    margin: 0 !important;
  }
}

// 전역 툴팁 스타일 (가장 강력)
.v-tooltip__content {
  background: rgba(97, 97, 97, 0.9);
  color: #ffffff;
  border-radius: 4px;
  font-size: 14px;
  line-height: 22px;
  display: inline-block;
  padding: 5px 16px;
  text-transform: initial;
  width: 280px;
  min-width: 280px;
  opacity: 1;
  pointer-events: none;
}
</style>
